import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Save, Webhook, Send, Check, Plus, Trash2, UserPlus, CalendarClock, MessageCircle, RefreshCw, Eye, Copy, AlertTriangle, KeyRound, UploadCloud } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { MONTHS_ID } from "../lib/format";

export default function Settings() {
  return (
    <>
      <PageHeader
        title="Pengaturan"
        subtitle="Info homestay, webhook reminder, user management, dan dynamic pricing rules."
      />
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-[#EBE6DE]/50 p-1 rounded-xl flex-wrap h-auto">
          <TabsTrigger value="general" data-testid="tab-settings-general" className="data-[state=active]:bg-white rounded-lg px-5">Umum</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-settings-users" className="data-[state=active]:bg-white rounded-lg px-5">Users</TabsTrigger>
          <TabsTrigger value="pricing" data-testid="tab-settings-pricing" className="data-[state=active]:bg-white rounded-lg px-5">Dynamic Pricing</TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-settings-whatsapp" className="data-[state=active]:bg-white rounded-lg px-5">WhatsApp Auto</TabsTrigger>
        </TabsList>
        <TabsContent value="general" className="mt-6"><GeneralTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
        <TabsContent value="pricing" className="mt-6"><PricingTab /></TabsContent>
        <TabsContent value="whatsapp" className="mt-6"><WhatsAppTab /></TabsContent>
      </Tabs>
    </>
  );
}

function GeneralTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get(`/settings`).then((r) => setForm(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/settings`, form); toast.success("Tersimpan."); }
    catch { toast.error("Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  const testWebhook = async () => {
    if (!form?.webhook_url) { toast.error("Isi Webhook URL dulu."); return; }
    setTesting(true);
    try {
      const r = await api.post(`/reminders/trigger-webhook?days=1`);
      toast.success(`Webhook terkirim! ${r.data.sent} reminder (status ${r.data.status_code}).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal.");
    } finally { setTesting(false); }
  };

  if (!form) return <div className="text-[#586D61]">Memuat…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Info Homestay</div>
          <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Detail untuk Invoice</h3>
        </div>
        <Field label="Nama Homestay" testid="input-homestay-name" value={form.homestay_name}
          onChange={(v) => setForm({ ...form, homestay_name: v })} />
        <Field label="Alamat" testid="input-homestay-address" value={form.homestay_address}
          onChange={(v) => setForm({ ...form, homestay_address: v })} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nomor Telepon" testid="input-homestay-phone" value={form.homestay_phone} mono
            onChange={(v) => setForm({ ...form, homestay_phone: v })} />
          <Field label="Email" testid="input-homestay-email" value={form.homestay_email}
            onChange={(v) => setForm({ ...form, homestay_email: v })} />
        </div>
        <div>
          <Label className="text-sm font-medium">Informasi Rekening / Pembayaran <span className="text-[#586D61] font-normal">(akan tampil di invoice)</span></Label>
          <div className="grid grid-cols-2 gap-3 mt-1.5">
            <Field label="Nama Bank" testid="input-bank-name" value={form.bank_name || ""}
              onChange={(v) => setForm({ ...form, bank_name: v })} />
            <Field label="Atas Nama" testid="input-bank-account-name" value={form.bank_account_name || ""}
              onChange={(v) => setForm({ ...form, bank_account_name: v })} />
          </div>
          <div className="mt-2">
            <Field label="Nomor Rekening" testid="input-bank-account-number" value={form.bank_account_number || ""} mono
              onChange={(v) => setForm({ ...form, bank_account_number: v })} />
          </div>
          <div className="mt-2">
            <Label className="text-xs text-[#586D61]">Catatan Pembayaran Tambahan (opsional)</Label>
            <Textarea
              data-testid="input-bank-info"
              value={form.bank_info || ""}
              onChange={(e) => setForm({ ...form, bank_info: e.target.value })}
              placeholder="mis: Mohon konfirmasi via WA setelah transfer."
              className="mt-1 border-[#EBE6DE]"
              rows={2}
            />
          </div>
        </div>

        <LogoUploader logoUrl={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} />
        <Button
          data-testid="btn-save-settings"
          onClick={save}
          disabled={saving}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
        >
          <Save className="h-4 w-4 mr-2" /> {saving ? "Menyimpan…" : "Simpan"}
        </Button>
      </div>

      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#D16B4D]/10 flex items-center justify-center text-[#D16B4D] flex-shrink-0">
            <Webhook className="h-5 w-5" strokeWidth={1.6} />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Automation</div>
            <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">Reminder H-1 via Webhook</h3>
            <p className="text-sm text-[#586D61] mt-1.5">
              Masukkan URL webhook dari n8n / Make / Zapier. Payload berisi daftar booking check-in besok beserta template pesan WhatsApp.
            </p>
          </div>
        </div>
        <Field label="Webhook URL" testid="input-webhook-url" value={form.webhook_url} mono
          placeholder="https://n8n.yourserver.com/webhook/..."
          onChange={(v) => setForm({ ...form, webhook_url: v })} />
        <Button
          data-testid="btn-test-webhook"
          onClick={testWebhook}
          disabled={testing}
          variant="outline"
          className="bg-white border-[#EBE6DE] hover:border-[#D16B4D] hover:text-[#D16B4D] rounded-xl"
        >
          <Send className="h-4 w-4 mr-2" /> {testing ? "Mengirim…" : "Test Webhook"}
        </Button>
        <div className="bg-[#FDFBF7] rounded-xl p-4 border border-[#EBE6DE] text-xs text-[#586D61] space-y-2">
          <div className="font-bold text-[#172E21] flex items-center gap-2">
            <Check className="h-3.5 w-3.5 text-[#2C7A51]" /> Endpoint publik untuk n8n cron:
          </div>
          <code className="block bg-white px-3 py-2 rounded-lg font-mono text-[#D16B4D] break-all">
            GET {API}/reminders/upcoming?days=1
          </code>
        </div>
      </div>
    </div>
  );
}


function LogoUploader({ logoUrl, onChange }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Ukuran logo maksimal 2MB");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.logo_url);
      toast.success("Logo terupload.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload logo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const handleRemove = async () => {
    try {
      await api.delete("/settings/logo");
      onChange("");
      toast.success("Logo dihapus.");
    } catch {
      toast.error("Gagal hapus logo");
    }
  };
  return (
    <div data-testid="logo-uploader">
      <Label className="text-sm font-medium">Logo Homestay <span className="text-[#586D61] font-normal">(muncul di invoice & laporan)</span></Label>
      <div className="flex items-center gap-4 mt-2">
        {logoUrl ? (
          <img src={logoUrl} alt="Logo" className="h-20 w-20 rounded-xl object-contain bg-[#FDFBF7] border border-[#EBE6DE]" data-testid="settings-logo-preview" />
        ) : (
          <div className="h-20 w-20 rounded-xl bg-[#FDFBF7] border-2 border-dashed border-[#EBE6DE] flex items-center justify-center text-[#586D61]">
            <UploadCloud className="h-8 w-8" />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <label className="cursor-pointer">
            <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="input-logo-upload" />
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[#EBE6DE] text-sm text-[#172E21] hover:bg-[#FDFBF7]">
              <UploadCloud className="h-4 w-4" /> {uploading ? "Mengunggah…" : (logoUrl ? "Ganti Logo" : "Upload Logo")}
            </span>
          </label>
          {logoUrl && (
            <Button type="button" variant="ghost" size="sm" onClick={handleRemove} className="text-[#D14D4D] hover:text-[#B83E3E] text-xs justify-start" data-testid="btn-remove-logo">
              Hapus logo
            </Button>
          )}
          <div className="text-[10px] text-[#586D61]">PNG/JPG, max 2MB. Disarankan 200×200 px.</div>
        </div>
      </div>
    </div>
  );
}


function Field({ label, value, onChange, testid, mono, placeholder }) {
  return (
    <div>
      <Label className="text-sm font-medium">{label}</Label>
      <Input
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`mt-1.5 border-[#EBE6DE] ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [deleteId, setDeleteId] = useState(null);
  const [pwDialog, setPwDialog] = useState(null);  // { user }
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });

  const load = () => api.get(`/users`).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error("Semua field wajib diisi.");
      return;
    }
    try {
      await api.post(`/users`, form);
      toast.success("User ditambahkan.");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "staff" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${deleteId}`);
      toast.success("User dihapus.");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal.");
      setDeleteId(null);
    }
  };

  const openChangePw = (u) => {
    setPwDialog({ user: u });
    setPwForm({ current_password: "", new_password: "", confirm: "" });
  };

  const submitChangePw = async () => {
    if (pwForm.new_password.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    try {
      await api.put(`/users/${pwDialog.user.id}/password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password berhasil diubah");
      setPwDialog(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mengubah password");
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button
          data-testid="btn-add-user"
          onClick={() => setOpen(true)}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
        >
          <UserPlus className="h-4 w-4 mr-2" /> Tambah User
        </Button>
      </div>

      <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Nama</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Email</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Role</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-[#FDFBF7]">
                <TableCell className="font-medium text-[#172E21]">{u.name}</TableCell>
                <TableCell className="font-mono text-sm text-[#586D61]">{u.email}</TableCell>
                <TableCell>
                  <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    u.role === "owner" ? "bg-[#D16B4D]/15 text-[#D16B4D]" : "bg-[#2C7A51]/15 text-[#2C7A51]"
                  }`}>
                    {u.role}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      data-testid={`btn-change-pw-${u.id}`}
                      variant="ghost" size="icon"
                      onClick={() => openChangePw(u)}
                      title="Ganti Password"
                      className="h-8 w-8 hover:bg-[#2C7A51]/10 hover:text-[#2C7A51]"
                    >
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    {u.role !== "owner" && (
                      <Button
                        data-testid={`btn-delete-user-${u.id}`}
                        variant="ghost" size="icon"
                        onClick={() => setDeleteId(u.id)}
                        className="h-8 w-8 hover:bg-[#D14D4D]/10 hover:text-[#D14D4D]"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Tambah User</DialogTitle>
            <DialogDescription className="text-[#586D61]">Staff akan punya akses terbatas (no Settings, Reports, Delete).</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <Field label="Nama" testid="input-new-user-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <Field label="Email" testid="input-new-user-email" value={form.email} mono onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Password" testid="input-new-user-password" value={form.password} mono onChange={(v) => setForm({ ...form, password: v })} />
            <div>
              <Label className="text-sm font-medium">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="select-new-user-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Batal</Button>
            <Button data-testid="btn-save-user" onClick={save} className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus User?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#586D61]">Akses user akan segera dicabut.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-user" onClick={doDelete}
              className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!pwDialog} onOpenChange={(v) => !v && setPwDialog(null)}>
        <DialogContent className="max-w-md bg-white" data-testid="dialog-change-password">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[#D16B4D]" /> Ganti Password
            </DialogTitle>
            <DialogDescription className="text-[#586D61]">
              {pwDialog?.user?.email}
              <span className="block mt-1 text-xs">Password baru minimal 8 karakter.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <div>
              <Label className="text-sm font-medium">Password Saat Ini</Label>
              <Input
                data-testid="input-current-password"
                type="password"
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="mt-1.5 font-mono"
                placeholder="••••••••"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Password Baru</Label>
              <Input
                data-testid="input-new-password"
                type="password"
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="mt-1.5 font-mono"
                placeholder="Minimal 8 karakter"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Konfirmasi Password Baru</Label>
              <Input
                data-testid="input-confirm-password"
                type="password"
                value={pwForm.confirm}
                onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
                className="mt-1.5 font-mono"
                placeholder="Ulangi password baru"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwDialog(null)} className="rounded-xl">Batal</Button>
            <Button
              data-testid="btn-submit-change-password"
              onClick={submitChangePw}
              className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
            >
              Simpan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PricingTab() {
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", start_month: 12, end_month: 2, multiplier: 1.3 });
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoadingRules(true);
    return api.get(`/season-rules`)
      .then((r) => setRules(r.data))
      .finally(() => setLoadingRules(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", start_month: 12, end_month: 2, multiplier: 1.3 });
    setOpen(true);
  };
  const openEdit = (rule) => {
    setEditing(rule);
    setForm({ name: rule.name, start_month: rule.start_month, end_month: rule.end_month, multiplier: rule.multiplier });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) { toast.error("Nama wajib diisi."); return; }
    try {
      const payload = {
        name: form.name,
        start_month: Number(form.start_month),
        end_month: Number(form.end_month),
        multiplier: Number(form.multiplier),
      };
      if (editing) await api.put(`/season-rules/${editing.id}`, payload);
      else await api.post(`/season-rules`, payload);
      toast.success("Tersimpan.");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal.");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/season-rules/${deleteId}`);
      toast.success("Rule dihapus.");
      setDeleteId(null);
      load();
    } catch { toast.error("Gagal."); setDeleteId(null); }
  };

  const pctSign = (mult) => {
    const pct = (mult - 1) * 100;
    return pct > 0 ? `+${pct.toFixed(0)}%` : `${pct.toFixed(0)}%`;
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-[#D99338]/10 border border-[#D99338]/30 rounded-2xl p-4 flex gap-3 items-start">
        <CalendarClock className="h-5 w-5 text-[#D99338] flex-shrink-0 mt-0.5" strokeWidth={1.6} />
        <div className="text-sm text-[#586D61]">
          <strong className="text-[#172E21]">Dynamic pricing:</strong> harga dasar kamar otomatis dikalikan dengan multiplier saat tanggal booking masuk season rule.
          Contoh: base rate Rp 1.000.000 × 1.3 (High Season Dec-Feb) = <strong className="text-[#D16B4D]">Rp 1.300.000/malam</strong>.
          Rentang bulan bisa "wrap" (Des-Feb = 12→2).
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          data-testid="btn-add-season-rule"
          onClick={openCreate}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl"
        >
          <Plus className="h-4 w-4 mr-2" /> Tambah Season Rule
        </Button>
      </div>

      <div className="bg-white border border-[#EBE6DE] rounded-2xl shadow-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FDFBF7] hover:bg-[#FDFBF7]">
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Nama</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Bulan Mulai</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider">Bulan Akhir</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Multiplier</TableHead>
              <TableHead className="text-[#586D61] font-bold uppercase text-[10px] tracking-wider text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingRules && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#586D61] py-8">Memuat…</TableCell></TableRow>
            )}
            {!loadingRules && rules.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#586D61] py-8">Belum ada season rule.</TableCell></TableRow>
            )}
            {rules.map((r) => (
              <TableRow key={r.id} data-testid={`season-row-${r.id}`} className="hover:bg-[#FDFBF7]">
                <TableCell className="font-medium text-[#172E21]">{r.name}</TableCell>
                <TableCell className="text-sm">{MONTHS_ID[r.start_month - 1]}</TableCell>
                <TableCell className="text-sm">{MONTHS_ID[r.end_month - 1]}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  <span className="font-bold text-[#D16B4D]">{r.multiplier.toFixed(2)}×</span>
                  <span className="ml-2 text-[#586D61]">({pctSign(r.multiplier)})</span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button data-testid={`btn-edit-season-${r.id}`} variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-8 w-8 hover:bg-[#2C7A51]/10 hover:text-[#2C7A51]">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                    </Button>
                    <Button data-testid={`btn-delete-season-${r.id}`} variant="ghost" size="icon" onClick={() => setDeleteId(r.id)} className="h-8 w-8 hover:bg-[#D14D4D]/10 hover:text-[#D14D4D]">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editing ? "Edit" : "Tambah"} Season Rule</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4 py-2">
            <Field label="Nama" testid="input-season-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium">Bulan Mulai</Label>
                <Select value={String(form.start_month)} onValueChange={(v) => setForm({ ...form, start_month: Number(v) })}>
                  <SelectTrigger data-testid="select-season-start" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS_ID.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium">Bulan Akhir</Label>
                <Select value={String(form.end_month)} onValueChange={(v) => setForm({ ...form, end_month: Number(v) })}>
                  <SelectTrigger data-testid="select-season-end" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS_ID.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Multiplier (e.g. 1.3 = +30%)</Label>
              <Input
                data-testid="input-season-multiplier"
                type="number" step="0.05" min={0.1}
                value={form.multiplier}
                onChange={(e) => setForm({ ...form, multiplier: e.target.value })}
                className="mt-1.5 border-[#EBE6DE] font-mono"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-xl">Batal</Button>
            <Button data-testid="btn-save-season" onClick={save} className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">Hapus Season Rule?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-season" onClick={doDelete}
              className="bg-[#D14D4D] hover:bg-[#B83E3E] text-white rounded-xl">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function WhatsAppTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [snapshotCfg, setSnapshotCfg] = useState(null);

  const loadAll = async () => {
    const [c, p, l, s] = await Promise.all([
      api.get("/whatsapp/config"),
      api.get("/whatsapp/preview"),
      api.get("/whatsapp/logs?limit=20"),
      api.get("/reports/snapshot-config").catch(() => ({ data: null })),
    ]);
    setConfig(c.data);
    setPreview(p.data);
    setLogs(l.data);
    setSnapshotCfg(s.data);
  };

  useEffect(() => { loadAll().catch(() => toast.error("Gagal memuat config WhatsApp")); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/whatsapp/config", config);
      toast.success("Config tersimpan");
      const p = await api.get("/whatsapp/preview");
      setPreview(p.data);
    } catch {
      toast.error("Gagal simpan");
    } finally {
      setSaving(false);
    }
  };

  const refreshPreview = async () => {
    setPreviewLoading(true);
    try {
      const p = await api.get("/whatsapp/preview");
      setPreview(p.data);
    } finally {
      setPreviewLoading(false);
    }
  };

  const testSend = async (booking_id) => {
    try {
      await api.post(`/whatsapp/test-send?booking_id=${booking_id}`);
      toast.success("Test reminder dikirim (lihat Log)");
      const l = await api.get("/whatsapp/logs?limit=20");
      setLogs(l.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim");
    }
  };

  const generateSecret = () => {
    const r = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    setConfig({ ...config, cron_secret: r });
  };

  if (!config) return <div className="text-[#586D61]">Memuat…</div>;

  const cronUrl = `${API}/whatsapp/run-reminders`;

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Config card */}
      <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm flex flex-col gap-5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#1B5E3F]/10 flex items-center justify-center text-[#1B5E3F] flex-shrink-0">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Auto-Reminder</div>
            <h3 className="font-heading text-xl font-medium text-[#172E21] mt-1">WhatsApp H-1 Otomatis</h3>
            <p className="text-sm text-[#586D61] mt-1.5">
              Kirim pengingat check-in otomatis sehari sebelum tamu datang.
            </p>
          </div>
        </div>

        {/* Status notice */}
        {config.provider === "mock" ? (
          <div className="rounded-xl bg-[#D99338]/8 border border-[#D99338]/30 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-[#D99338] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#586D61]">
              <span className="font-bold text-[#172E21]">Mode MOCK aktif.</span>{" "}
              Pesan TIDAK benar-benar terkirim — hanya tercatat di log.
              Ganti provider ke Fonnte/Twilio untuk produksi.
            </div>
          </div>
        ) : config.provider === "fonnte" ? (
          <div className="rounded-xl bg-emerald-50 border border-emerald-300 p-4 flex items-start gap-3">
            <Check className="h-4 w-4 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-emerald-900">
              <span className="font-bold">Fonnte aktif.</span>{" "}
              Pesan akan benar-benar terkirim via WhatsApp. Pastikan device Fonnte Anda ter-scan dan online di dashboard fonnte.com.
              <div className="mt-1.5 text-[11px] text-emerald-700">
                Token bisa dikosongkan di sini jika sudah di-set via environment variable <code className="bg-white px-1 py-0.5 rounded">FONNTE_TOKEN</code>.
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-xl bg-cyan-50 border border-cyan-300 p-4 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-cyan-700 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-cyan-900">
              <span className="font-bold">Twilio masih mock.</span>{" "}
              Implementasi Twilio disabled — silakan pakai Fonnte.
            </div>
          </div>
        )}

        {/* Toggle enabled */}
        <div className="flex items-center justify-between p-4 rounded-xl border border-[#EBE6DE] bg-[#FDFBF7]">
          <div>
            <div className="font-heading text-sm font-semibold text-[#172E21]">
              {config.enabled ? "Aktif" : "Nonaktif"}
            </div>
            <div className="text-xs text-[#586D61] mt-0.5">
              Saat aktif, cron H-1 akan kirim reminder otomatis.
            </div>
          </div>
          <Switch
            data-testid="toggle-wa-enabled"
            checked={!!config.enabled}
            onCheckedChange={(v) => setConfig({ ...config, enabled: v })}
          />
        </div>

        {/* Per-event toggles */}
        <div className="rounded-xl border border-[#EBE6DE] bg-white p-4 space-y-3">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Event Auto-Send</div>
          <EventToggle
            testid="toggle-wa-event-confirm"
            label="Konfirmasi Booking"
            hint="Otomatis saat booking baru dibuat"
            checked={config.send_on_confirm !== false}
            onChange={(v) => setConfig({ ...config, send_on_confirm: v })}
          />
          <EventToggle
            testid="toggle-wa-event-payment"
            label="Reminder Pembayaran H-3"
            hint="3 hari sebelum check-in, jika belum lunas"
            checked={config.send_on_payment !== false}
            onChange={(v) => setConfig({ ...config, send_on_payment: v })}
          />
          <EventToggle
            testid="toggle-wa-event-reminder"
            label="Reminder Check-in H-1"
            hint="Sehari sebelum tamu datang"
            checked={config.send_on_reminder !== false}
            onChange={(v) => setConfig({ ...config, send_on_reminder: v })}
          />
        </div>

        {/* Provider */}
        <div>
          <Label className="text-sm font-medium">Provider</Label>
          <Select value={config.provider} onValueChange={(v) => setConfig({ ...config, provider: v })}>
            <SelectTrigger data-testid="select-wa-provider" className="mt-1.5 border-[#EBE6DE]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mock">Mock (testing — tidak benar-benar kirim)</SelectItem>
              <SelectItem value="fonnte">Fonnte (Indonesia)</SelectItem>
              <SelectItem value="twilio">Twilio (Global)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Twilio SID — only shown when twilio */}
        {config.provider === "twilio" && (
          <Field
            label="Twilio Account SID"
            value={config.twilio_account_sid}
            onChange={(v) => setConfig({ ...config, twilio_account_sid: v })}
            testid="input-wa-twilio-sid"
            mono
            placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
          />
        )}

        {/* API Token */}
        {config.provider !== "mock" && (
          <Field
            label={config.provider === "twilio" ? "Twilio Auth Token" : "Fonnte API Token"}
            value={config.api_token}
            onChange={(v) => setConfig({ ...config, api_token: v })}
            testid="input-wa-api-token"
            mono
            placeholder={config.provider === "twilio" ? "your_auth_token" : "xxxxxxxxxxxxxxxxxxxxxxx"}
          />
        )}

        {/* Sender number */}
        {config.provider !== "mock" && (
          <Field
            label="Nomor Sender (62…)"
            value={config.sender_number}
            onChange={(v) => setConfig({ ...config, sender_number: v })}
            testid="input-wa-sender"
            mono
            placeholder="6281234567890"
          />
        )}

        {/* Cron secret */}
        <div>
          <Label className="text-sm font-medium">Cron Secret</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              data-testid="input-wa-cron-secret"
              value={config.cron_secret}
              onChange={(e) => setConfig({ ...config, cron_secret: e.target.value })}
              placeholder="Klik 'Generate' untuk buat random secret"
              className="border-[#EBE6DE] font-mono"
            />
            <Button type="button" variant="outline" onClick={generateSecret} data-testid="btn-wa-gen-secret">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-xs text-[#586D61] mt-1">
            Header <code className="bg-[#EBE6DE]/50 px-1 rounded">X-Cron-Secret</code> wajib diisi saat trigger cron eksternal.
          </p>
        </div>

        {/* Cron URL + Setup Guide */}
        <div className="rounded-xl bg-[#082618]/5 border border-[#082618]/20 p-3 space-y-2.5">
          <Label className="text-[10px] font-bold uppercase tracking-wider text-[#586D61]">URL Cron Trigger</Label>
          <div className="flex items-center gap-2">
            <code data-testid="wa-cron-url" className="flex-1 text-xs font-mono text-[#172E21] truncate">{cronUrl}</code>
            <Button
              type="button" variant="outline" size="sm"
              data-testid="btn-copy-cron-url"
              onClick={() => { navigator.clipboard.writeText(cronUrl); toast.success("URL tersalin"); }}
              className="h-8 w-8 p-0"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>

          {/* Cron-job.org setup guide */}
          <details className="group" data-testid="cron-setup-guide">
            <summary className="cursor-pointer text-xs font-bold text-[#D16B4D] hover:text-[#B8553B] flex items-center gap-1.5 py-1">
              <CalendarClock className="h-3.5 w-3.5" />
              <span>Panduan setup cron eksternal (cron-job.org — GRATIS)</span>
            </summary>
            <div className="mt-2 pl-4 border-l-2 border-[#D16B4D]/30 text-xs text-[#586D61] space-y-2">
              <ol className="list-decimal list-outside space-y-1.5 ml-4">
                <li>Daftar gratis di <a href="https://cron-job.org" target="_blank" rel="noreferrer" className="text-[#D16B4D] underline font-medium">cron-job.org</a> (tanpa kartu kredit)</li>
                <li>Klik <strong>Create cronjob</strong></li>
                <li>
                  <strong>URL</strong>: tempel URL di atas ↑
                </li>
                <li>
                  <strong>Title</strong>: <code className="bg-white px-1 rounded">PondokRaja WA Reminder</code>
                </li>
                <li>
                  <strong>Schedule</strong>: pilih tab <em>Custom</em>, set <code>Every day</code> jam <code>09:00</code> (akan jadi jam 16:00 WIB — server cron-job.org UTC).
                  Atau pakai jam <code>02:00 UTC</code> = jam 09:00 WIB.
                </li>
                <li>
                  <strong>Advanced → Request method</strong>: <code>POST</code>
                </li>
                <li>
                  <strong>Advanced → Request headers</strong>: klik <code>+</code> tambah:
                  <div className="bg-white rounded-lg border p-2 mt-1 font-mono text-[10px]">
                    <div>Name: <code>X-Cron-Secret</code></div>
                    <div>Value: <code className="break-all">{config.cron_secret || "<generate cron secret dulu>"}</code></div>
                  </div>
                </li>
                <li>Save → cron akan jalan otomatis setiap pagi.</li>
              </ol>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-900 mt-2">
                💡 <strong>Alternatif gratis lain</strong>: GitHub Actions (scheduled workflow), Vercel Cron, EasyCron, n8n self-host.
              </div>
            </div>
          </details>

          {/* cURL template for copy */}
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-wider text-[#586D61]">cURL Test (copy paste di terminal)</Label>
            <div className="flex items-start gap-2 mt-1">
              <code className="flex-1 text-[10px] font-mono bg-white rounded-lg border border-[#EBE6DE] p-2 break-all">
                {`curl -X POST "${cronUrl}" -H "X-Cron-Secret: ${config.cron_secret || "<your-secret>"}"`}
              </code>
              <Button
                type="button" variant="outline" size="sm"
                data-testid="btn-copy-curl"
                onClick={() => {
                  const cmd = `curl -X POST "${cronUrl}" -H "X-Cron-Secret: ${config.cron_secret || ""}"`;
                  navigator.clipboard.writeText(cmd);
                  toast.success("cURL command tersalin");
                }}
                className="h-8 w-8 p-0 flex-shrink-0"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Template */}
        <div>
          <Label className="text-sm font-medium">Template Pesan</Label>
          <Select value={config.template} onValueChange={(v) => setConfig({ ...config, template: v })}>
            <SelectTrigger data-testid="select-wa-template" className="mt-1.5 border-[#EBE6DE]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default (Halo... BESOK check-in...)</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          {config.template === "custom" && (
            <Textarea
              data-testid="input-wa-custom-template"
              rows={5}
              value={config.custom_template}
              onChange={(e) => setConfig({ ...config, custom_template: e.target.value })}
              placeholder={`Halo {guest_name}, besok {check_in_date} Anda check-in di {homestay_name}...`}
              className="mt-2 border-[#EBE6DE] font-mono text-xs"
            />
          )}
          <p className="text-xs text-[#586D61] mt-1">
            Variable: {`{guest_name}`}, {`{room_name}`}, {`{check_in_date}`}, {`{guests}`}, {`{homestay_name}`}
          </p>
        </div>

        <Button
          data-testid="btn-save-wa-config"
          onClick={save}
          disabled={saving}
          className="bg-[#D16B4D] hover:bg-[#B8553B] text-white rounded-xl gap-2 self-start"
        >
          <Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Konfig"}
        </Button>
      </div>

      {/* Preview & Logs */}
      <div className="space-y-6">
        {/* Preview card */}
        <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Preview</div>
              <h3 className="font-heading text-lg font-medium text-[#172E21] mt-1">Akan Dikirim Besok</h3>
              <p className="text-xs text-[#586D61] mt-1">Tanggal: {preview?.tomorrow}</p>
            </div>
            <Button
              type="button" variant="outline" size="sm"
              data-testid="btn-refresh-preview"
              onClick={refreshPreview}
              disabled={previewLoading}
            >
              <RefreshCw className={`h-4 w-4 ${previewLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {!preview || preview.count === 0 ? (
            <div className="text-center py-6 text-sm text-[#586D61]">
              Tidak ada booking yang check-in besok.
            </div>
          ) : (
            <div className="space-y-2" data-testid="wa-preview-list">
              {preview.items.map((it) => (
                <div
                  key={it.booking_id}
                  data-testid={`wa-preview-${it.booking_id}`}
                  className="border border-[#EBE6DE] rounded-xl p-3 bg-[#FDFBF7]"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="font-heading text-sm font-semibold text-[#172E21]">
                        {it.guest_name}
                      </div>
                      <div className="text-xs text-[#586D61]">
                        {it.room_name} • {it.phone_normalized || "no phone"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {it.would_send ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#1B5E3F]/10 text-[#1B5E3F]">
                          <Check className="h-3 w-3" /> READY
                        </span>
                      ) : (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#D99338]/15 text-[#A66A1F]">
                          NO PHONE
                        </span>
                      )}
                      {it.would_send && (
                        <Button
                          size="sm" variant="outline"
                          data-testid={`btn-test-send-${it.booking_id}`}
                          onClick={() => testSend(it.booking_id)}
                          className="h-7 px-2 text-xs gap-1"
                        >
                          <Send className="h-3 w-3" /> Test
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Logs card */}
        <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm">
          <div className="mb-4">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Log Pengiriman</div>
            <h3 className="font-heading text-lg font-medium text-[#172E21] mt-1">20 Pengiriman Terakhir</h3>
          </div>
          {logs.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#586D61]">Belum ada log.</div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="wa-logs-list">
              {logs.map((l) => {
                const cls = {
                  sent: "bg-[#1B5E3F]/10 text-[#1B5E3F]",
                  mocked: "bg-[#082618]/8 text-[#082618]",
                  failed: "bg-[#D14D4D]/10 text-[#D14D4D]",
                  skipped: "bg-[#D99338]/15 text-[#A66A1F]",
                }[l.status] || "bg-gray-100 text-gray-600";
                return (
                  <div key={l.id} data-testid={`wa-log-${l.id}`} className="border border-[#EBE6DE] rounded-lg p-2.5">
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <div className="font-medium text-[#172E21]">{l.guest_name}</div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${cls}`}>
                        {l.status}
                      </span>
                    </div>
                    <div className="text-[10px] text-[#586D61] mt-1 font-mono">
                      {l.phone_number || "—"} • {l.provider} • {new Date(l.created_at).toLocaleString("id-ID")}
                    </div>
                    {l.error && <div className="text-[10px] text-[#D14D4D] mt-1">{l.error}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Auto Report (n8n) snapshot */}
        {snapshotCfg && (
          <div className="bg-white border border-[#EBE6DE] rounded-2xl p-6 shadow-sm" data-testid="snapshot-card">
            <div className="mb-4">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#586D61]">Auto Report</div>
              <h3 className="font-heading text-lg font-medium text-[#172E21] mt-1">PDF Bulanan Otomatis (n8n)</h3>
              <p className="text-xs text-[#586D61] mt-1">
                URL HTML laporan untuk n8n / cron — convert ke PDF lalu kirim ke email/WA.
              </p>
            </div>

            {!snapshotCfg.configured ? (
              <div className="rounded-lg bg-[#D14D4D]/8 border border-[#D14D4D]/30 p-3 text-xs text-[#D14D4D]">
                <span className="font-bold">Belum dikonfigurasi.</span> Tambahkan{" "}
                <code className="bg-white px-1 rounded">REPORTS_SNAPSHOT_TOKEN</code> di backend/.env.
              </div>
            ) : (
              <div className="space-y-2.5">
                <div className="text-[10px] text-[#586D61] mb-1">
                  Token: <code className="bg-[#EBE6DE]/50 px-1 rounded font-mono">{snapshotCfg.token_preview}</code>
                </div>
                {Object.entries(snapshotCfg.examples).map(([k, url]) => (
                  <div key={k} className="border border-[#EBE6DE] rounded-lg p-2.5 bg-[#FDFBF7]">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#586D61]">{k.replace("_", " ")}</span>
                      <Button
                        type="button" variant="outline" size="sm"
                        data-testid={`btn-copy-snapshot-${k}`}
                        onClick={() => { navigator.clipboard.writeText(`${API}${url}`); toast.success("URL tersalin"); }}
                        className="h-6 w-6 p-0"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                    <code className="text-[10px] font-mono text-[#172E21] break-all">{API}{url}</code>
                  </div>
                ))}
                <div className="rounded-lg bg-[#082618]/5 border border-[#082618]/20 p-3 text-[10px] text-[#586D61] mt-3 leading-relaxed">
                  <div className="font-bold text-[#172E21] mb-1">Cara setup di n8n:</div>
                  1. Trigger: Cron node — schedule "0 8 1 * *" (jam 8 pagi tgl 1)<br/>
                  2. HTTP Request: GET ke URL "monthly auto" → output HTML<br/>
                  3. HTML to PDF node (n8n built-in) → output binary PDF<br/>
                  4. Email/WA send → attach PDF
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



function EventToggle({ testid, label, hint, checked, onChange }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-[#172E21]">{label}</div>
        <div className="text-[11px] text-[#586D61]">{hint}</div>
      </div>
      <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
